import { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useOnClickOutside from "@src/common/hooks/useOnClickOutside";
import {
  AddCustom,
  AddOption,
  DropdownBadge,
  DropdownComponentWrapper,
  DropdownLabel,
  DropdownOption,
  DropdownStyle,
  DropdownToggle,
  DropdownWrapper,
  OptionName,
  OptionNameWrapper,
  OptionsWrapper,
  ScrollWrapper,
  SearchInput,
  StyledDropdown,
  TextWrapper,
} from "@src/modules/shared/components/Dropdown/Dropdown.components";
import useDropdown from "@src/modules/shared/components/Dropdown/useDropdown";
import ErrorMessage from "@src/modules/shared/components/Form/ErrorMessage";
import Checkbox from "@src/modules/shared/components/Form/Checkbox";

export interface DropdownOption<T> {
  name: string;
  value: T;
  badge?: ReactElement;
  disabled?: boolean;
}

export interface DropdownProps<T> {
  options: DropdownOption<T>[];
  value?: T | T[];
  placeholder?: string;
  label?: string;
  style?: DropdownStyle;
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  showError?: boolean;
  className?: string;
  addOption?: ReactElement;
  onChange: (value: T) => void;
  onClose?: () => void;
  multi?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  canAddCustom?: boolean;
  customFieldValidationRegex?: RegExp;
  parseCustomValue?: (input: string) => T;
  fullWidthOption?: boolean;
}

const Dropdown = <T,>({
  options,
  value,
  placeholder,
  label,
  style = "lightBig",
  isLoading,
  disabled,
  error,
  showError = false,
  className,
  addOption,
  onChange,
  onClose,
  multi,
  searchable,
  canAddCustom,
  customFieldValidationRegex,
  parseCustomValue,
  searchPlaceholder,
  fullWidthOption,
}: DropdownProps<T>) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdown = useDropdown();
  const [searchValue, setSearchValue] = useState<string>("");
  const [internalOptions, setInternalOptions] = useState<DropdownOption<T>[]>(
    [],
  );
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onClickOutside = () => {
    if (dropdown.isOpen) {
      dropdown.setIsOpen(false);
      setSearchValue("");
      onClose?.();
    }
  };

  useEffect(() => {
    setInternalOptions(options);
  }, [options]);

  useEffect(() => {
    if (dropdown.isOpen && !!searchable) {
      const timeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timeout);
    } else if (!dropdown.isOpen) {
      setSearchValue("");
    }
  }, [dropdown.isOpen, searchable]);

  useOnClickOutside(dropdownRef, onClickOutside);

  const handleOptionClick = (
    option: DropdownOption<T>,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    if (!option.disabled) {
      if (multi) {
        const currentValues = Array.isArray(value) ? value : [];
        const isSelected = currentValues.some((v) => v === option.value);

        const newValues = isSelected
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value];

        onChange(newValues as T);
      } else {
        onChange(option.value);
        dropdown.setIsOpen(false);
      }
    }
  };

  const onCurrentOptionClick = () => {
    if (disabled) {
      return;
    }
    if (dropdown.isOpen) {
      onClose?.();
    }
    dropdown.toggle();
  };

  const current =
    !!multi && value
      ? {
          name: `${internalOptions
            .filter(
              (option) =>
                Array.isArray(value) && value.some((v) => option.value === v),
            )
            .map((option) => option.name)
            .join(", ")}`,
        }
      : value
      ? internalOptions.find(
          (option: DropdownOption<T>) => option.value === value,
        )
      : undefined;

  const filteredOptions = !!searchable
    ? internalOptions.filter((o) =>
        o.name.toLocaleLowerCase().includes(searchValue?.toLocaleLowerCase()),
      )
    : internalOptions;

  const handleAddCustom = () => {
    const newValue = !!parseCustomValue
      ? parseCustomValue(searchValue)
      : (searchValue as T);
    const newOption = {
      name: searchValue,
      value: newValue,
    } as DropdownOption<T>;
    setInternalOptions([...internalOptions, newOption]);
    onChange(newValue);
    dropdown.setIsOpen(false);
    setSearchValue("");
  };

  const addCustom =
    !!canAddCustom && searchValue && filteredOptions.length === 0 ? (
      <AddOption>
        <AddCustom
          disabled={
            !!customFieldValidationRegex &&
            !customFieldValidationRegex.test(searchValue)
          }
          onClick={handleAddCustom}
        >
          {t("Add")}
        </AddCustom>
      </AddOption>
    ) : null;

  const currentOption = (
    <DropdownOption
      onClick={onCurrentOptionClick}
      $isOpen={dropdown.isOpen}
      isLoading={isLoading}
      dropdownStyle={style}
      isCurrent={true}
      isError={!!error}
      disabled={disabled}
    >
      {label && <DropdownLabel>{label}</DropdownLabel>}
      <OptionNameWrapper isBoldStyle={style === "bold"}>
        <TextWrapper isPlaceholder={!current?.name}>
          {current?.name || placeholder}
        </TextWrapper>
        {!isLoading && (
          <DropdownToggle $isOpen={dropdown.isOpen} disabled={disabled} />
        )}
      </OptionNameWrapper>
    </DropdownOption>
  );

  const dropdownOptions = filteredOptions.map((option, index) => (
    <DropdownOption
      key={index}
      onClick={(e) => handleOptionClick(option, e)}
      dropdownStyle={style}
      hasBadge={!!option.badge}
      disabled={option.disabled}
    >
      {!!multi ? (
        <Checkbox
          name={option.name}
          hasBigLabel
          checked={
            Array.isArray(value) ? value.some((v) => v === option.value) : false
          }
          stopPropagation={multi}
        >
          <OptionName>{option.name}</OptionName>
        </Checkbox>
      ) : (
        <>
          <OptionName fullWidth={fullWidthOption}>{option.name}</OptionName>
          {option.badge && <DropdownBadge>{option.badge}</DropdownBadge>}
        </>
      )}
    </DropdownOption>
  ));

  return (
    <DropdownComponentWrapper
      className={className}
      isError={!!error}
      showError={showError}
      ref={dropdownRef}
    >
      <DropdownWrapper dropdownStyle={style}>
        <StyledDropdown
          $isOpen={dropdown.isOpen}
          dropdownStyle={style}
          isLabel={!!label}
        >
          {currentOption}
          <OptionsWrapper dropdownStyle={style} $isOpen={dropdown.isOpen}>
            <ScrollWrapper>
              {!!searchable && (
                <SearchInput
                  name="search"
                  ref={searchInputRef}
                  onChange={(e) => setSearchValue(e.target.value)}
                  value={searchValue}
                  autoFocus
                  placeholder={searchPlaceholder}
                />
              )}
              {dropdownOptions}
              {addCustom}
              {addOption && (
                <AddOption onClick={() => dropdown.setIsOpen(false)}>
                  {addOption}
                </AddOption>
              )}
            </ScrollWrapper>
          </OptionsWrapper>
        </StyledDropdown>
      </DropdownWrapper>
      {error && showError && <ErrorMessage>{error}</ErrorMessage>}
    </DropdownComponentWrapper>
  );
};

export default Dropdown;
